import React, { Component } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import classNames from 'classnames';

import { change } from 'redux-form';
import { ModalHOC } from 'common/HOCs/ModalHOC';
// import { DefaultButton } from 'common/components';
import { NoItemsPanel } from 'common/components';
import { taggedStylesToString, withTheme } from 'common/styling/theme';

// import { BasketIcon } from 'common/icons';
import FormFieldsComposer from '../FormFieldsComposer';

import { getDynamicStyles, staticStyles } from './style';

class MasterGroupArrayField extends Component {
  // 深拷贝函数，不会序列化函数
  static deepClone(obj) {
    if (obj === null || typeof obj !== 'object') {
      return obj;
    }
    if (obj instanceof Date) {
      return new Date(obj.getTime());
    }
    if (obj instanceof Array) {
      return obj.map(item => MasterGroupArrayField.deepClone(item));
    }
    if (typeof obj === 'object') {
      const clonedObj = {};
      Object.keys(obj).forEach(key => {
        clonedObj[key] = MasterGroupArrayField.deepClone(obj[key]);
      });
      return clonedObj;
    }
  }

  // 动态创建包含condition字段的schema
  static createDynamicSchema(originalSchema, conditions) {
    if (!originalSchema || !originalSchema.items || !conditions || conditions.length === 0) {
      return originalSchema;
    }
    // 创建schema的深拷贝
    const dynamicSchema = MasterGroupArrayField.deepClone(originalSchema);
    // 确保fields对象存在
    if (!dynamicSchema.items.properties) {
      dynamicSchema.items.properties = {};
    }

    // 将condition字段添加到reward的items.properties中，而非与name同级
    const rewardField = dynamicSchema.items.properties.reward;
    if (rewardField && rewardField.items) {
      if (!rewardField.items.properties) {
        rewardField.items.properties = {};
      }
      conditions.forEach((condition, index) => {
        const fieldName = `condition${index + 1}`;
        const conditionLabel = typeof condition === 'string' ? condition : `Condition ${index + 1}`;

        rewardField.items.properties[fieldName] = {
          widget: 'number',
          type: 'number',
          title: conditionLabel,
          default: 0,
          minimum: 0,
          required: true,
          disabled: false,
        };
      });
    }

    // 设置name字段为只读
    if (dynamicSchema.items.properties.name) {
      dynamicSchema.items.properties.name.disabled = true;
    }
    return dynamicSchema;
  }

  // 保留所有字段，包括name字段（已设为只读）
  static filterSchemaFields(schema) {
    if (!schema || !schema.items || !schema.items.fields) return schema;
    // 创建schema的深拷贝
    const filteredSchema = MasterGroupArrayField.deepClone(schema);
    // 保留所有字段，不再过滤name字段
    return filteredSchema;
  }

  static isValidGroup(group) {
    return (
      (typeof group === 'string' && group.trim().length > 0) || (typeof group === 'number' && Number.isFinite(group))
    );
  }

  static getGroupOptions(schema) {
    const options = schema?.groupOptions || schema?.group_options || [];
    return Array.isArray(options) ? options : [];
  }

  static getGroupName(group, groupOptions = []) {
    const option = groupOptions.find(item => String(item.value) === String(group));
    return option ? option.label : String(group);
  }

  static findRewardAmountItem(rewardAmount, group, groupName) {
    return (
      rewardAmount.find(
        item => item && (String(item.name) === String(group) || String(item.name) === String(groupName))
      ) || {}
    );
  }

  componentDidMount() {
    // 页面初始化时获取所有值
    // 检查当前fields是否已经匹配selectedGroups，避免不必要的重建导致数据清空
    const { fields, formData, index } = this.props;
    const currentFields = fields.getAll() || [];
    const selectedGroups = formData?.regular_account?.group_exceptions?.[index]?.value?.item || [];
    const groupOptions = MasterGroupArrayField.getGroupOptions(this.props.schema);
    const currentNames = currentFields.map(f => f?.name).filter(Boolean);
    const selectedNames = selectedGroups.map(group => MasterGroupArrayField.getGroupName(group, groupOptions));

    // 只有当fields为空或name列表与selectedGroups不匹配时才重新生成
    if (currentFields.length === 0 || JSON.stringify(currentNames) !== JSON.stringify(selectedNames)) {
      this.handleNextButtonClick(index);
    }
  }

  componentDidUpdate(prevProps) {
    // 当formData发生变化时，重新更新数据
    const { formData, index } = this.props;

    // 当外层group_exceptions数组长度变化（添加/删除组）时，
    // 只有当本组的关键数据确实变化了才重新生成，避免其它组数据被清空
    const currentExceptions = formData?.regular_account?.group_exceptions || [];
    const prevExceptions = prevProps.formData?.regular_account?.group_exceptions || [];

    // 如果本组的索引超出了数组范围，说明本组被删除了，不需要处理
    if (index >= currentExceptions.length) {
      return;
    }

    // 检查是否真的需要更新fields数据
    // 只有当selectedGroups或conditions发生变化时才更新
    const prevSelectedGroups = prevExceptions[index]?.value?.item || [];
    const currentSelectedGroups = currentExceptions[index]?.value?.item || [];

    const prevConditions = prevExceptions[index]?.value?.value?.condition || [];
    const currentConditions = currentExceptions[index]?.value?.value?.condition || [];

    // 只比较reward_amount的name列表，不比较内部reward数据的变化
    // 避免内嵌reward ArrayField的Add/Remove操作触发整体重新生成
    const prevRewardAmount = prevExceptions[index]?.value?.value?.reward_amount || [];
    const currentRewardAmount = currentExceptions[index]?.value?.value?.reward_amount || [];
    const prevRewardNames = prevRewardAmount.map(item => item?.name).filter(Boolean);
    const currentRewardNames = currentRewardAmount.map(item => item?.name).filter(Boolean);
    const groupOptionsChanged =
      JSON.stringify(MasterGroupArrayField.getGroupOptions(this.props.schema)) !==
      JSON.stringify(MasterGroupArrayField.getGroupOptions(prevProps.schema));

    // 只有当关键数据发生变化时才更新fields
    const groupsChanged = JSON.stringify(prevSelectedGroups) !== JSON.stringify(currentSelectedGroups);
    const conditionsChanged = JSON.stringify(prevConditions) !== JSON.stringify(currentConditions);
    const rewardNamesChanged = JSON.stringify(prevRewardNames) !== JSON.stringify(currentRewardNames);

    if (groupsChanged || conditionsChanged || rewardNamesChanged || groupOptionsChanged) {
      this.handleNextButtonClick(index);
    }
  }

  onDragEnd = result => {
    const { fields } = this.props;

    // dropped outside the list
    if (!result.destination) {
      return;
    }

    const startIndex = result.source.index;
    const endIndex = result.destination.index;
    const [removed] = fields.getAll().splice(startIndex, 1);

    fields.splice(endIndex, 0, removed);
  };

  // Next按钮
  handleNextButtonClick = index => {
    // 如果传入了index参数，使用传入的值；否则使用props中的index（如果有的话），如果都没有则使用默认值0
    const targetIndex = index !== undefined ? index : this.props.index !== undefined ? this.props.index : 0;
    const { fields, formData, dispatch, formName } = this.props;

    // 尝试多种可能的路径获取选中的group items
    let selectedGroups = [];
    let conditions = [];
    console.log('group_exceptions', formData?.regular_account?.group_exceptions);
    // 主路径：从regular_account.group_exceptions获取
    if (formData?.regular_account?.group_exceptions && formData.regular_account.group_exceptions.length > 0) {
      const firstException = formData.regular_account.group_exceptions[targetIndex];
      selectedGroups = firstException?.value?.item || [];
      conditions = firstException?.value?.value?.condition || [];
    }
    const rewardAmount = formData?.regular_account?.group_exceptions?.[targetIndex]?.value?.value?.reward_amount;
    const rewardAmountItems = Array.isArray(rewardAmount) ? rewardAmount : [];
    const groupOptions = MasterGroupArrayField.getGroupOptions(this.props.schema);

    // 清理 fix 类型下的结构：value 仅保留 item 与 value（内部自带 condition / reward_amount 等）
    const currentType = formData?.regular_account?.group_exceptions?.[targetIndex]?.type;
    if (currentType === 'fix') {
      const originalValue = formData?.regular_account?.group_exceptions?.[targetIndex]?.value || {};
      // 确保只保留 item 和 value 字段
      const cleanedValue = {
        item: originalValue.item || [],
        value: originalValue.value || {},
      };
      if (JSON.stringify(originalValue) !== JSON.stringify(cleanedValue)) {
        dispatch(change(formName, `regular_account.group_exceptions[${targetIndex}].value`, cleanedValue));
        // 同步更新本地 selectedGroups / conditions
        selectedGroups = cleanedValue.item;
        conditions = cleanedValue.value?.condition || [];
      }
    }
    // 清空现有的所有字段，重新生成数据
    const currentFields = fields.getAll() || [];
    console.log('currentFields', currentFields);
    // 从后往前删除，避免索引变化问题
    for (let i = currentFields.length - 1; i >= 0; i -= 1) {
      fields.remove(i);
    }
    // 为每个选中的group重新创建奖励条目
    selectedGroups.forEach(group => {
      if (MasterGroupArrayField.isValidGroup(group)) {
        const groupName = MasterGroupArrayField.getGroupName(group, groupOptions);
        const existingItem = MasterGroupArrayField.findRewardAmountItem(rewardAmountItems, group, groupName);
        let rewardData;

        if (existingItem && Array.isArray(existingItem.reward) && existingItem.reward.length > 0) {
          // 完整保留现有的reward数据
          rewardData = existingItem.reward;
        } else {
          // 构建新的reward数组项，将condition字段设置到reward的fields内
          const rewardItem = {};
          conditions.forEach((_, index) => {
            const conditionKey = `condition${index + 1}`;
            // 兼容旧数据从reward_amount项顶层获取
            const existingValue = existingItem[conditionKey];
            rewardItem[conditionKey] = existingValue !== undefined ? existingValue : null;
          });
          rewardData = [rewardItem];
        }

        const newItem = {
          name: groupName, // 设置name为展示名称，不保存Group ID
          reward: rewardData, // 完整保留reward数据
        };

        fields.push(newItem);
      }
    });
  };

  render() {
    const {
      schema,
      fields,
      meta: { error, pristine },
      formName,
      arraysName,
      submitting,
      theme,
      formData,
      index,
    } = this.props;

    const style = taggedStylesToString(staticStyles);
    const dynamicStyles = Object.keys(theme).length ? taggedStylesToString(getDynamicStyles(theme)) : ` `;

    const formError = !pristine && error;
    const isDragDisabled = !schema.ordering || fields.length === 1;
    const isFormError = typeof error === 'object';

    // 获取conditions数据
    let conditions = [];
    if (formData?.regular_account?.group_exceptions && formData.regular_account.group_exceptions.length > 0) {
      const firstException = formData.regular_account.group_exceptions[index]?.value;
      conditions = firstException?.value?.condition || [];
    }
    // 创建动态schema，包含condition字段
    const dynamicSchema = MasterGroupArrayField.createDynamicSchema(schema, conditions);

    // 过滤schema，移除name字段
    const filteredSchema = MasterGroupArrayField.filterSchemaFields(dynamicSchema);
    return (
      <div className="FieldArray" id={arraysName}>
        <div className="FieldArray__top-line">
          <div className="FieldArray__title">
            <FormattedMessage id={schema.title}>{txt => <h2>{txt}</h2>}</FormattedMessage>
          </div>
        </div>

        {fields.length ? (
          <DragDropContext onDragEnd={this.onDragEnd}>
            <Droppable droppableId="droppable" isDropDisabled={isDragDisabled}>
              {(provided, snapshot) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className={classNames('FieldArray__isDragging-inner', { isDraggingOver: snapshot.isDraggingOver })}>
                  {fields.map((item, index) => {
                    // 获取当前item的name作为唯一key
                    const itemName = fields.getAll()[index]?.name || `item_${index}`;
                    return (
                      <Draggable key={itemName} draggableId={item} index={index} isDragDisabled={isDragDisabled}>
                        {(provided, snapshot) => (
                          <div
                            className={classNames('FieldArray__item-inner', {
                              FieldArray__notDraggable: !schema.ordering,
                              FieldArray__isDraggable: snapshot.isDragging,
                              FieldArray__item_error: isFormError && error[index],
                            })}
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            style={provided.draggableProps.style}>
                            <div className="FieldArray__item">
                              <p className="FieldArray__item-number">{index + 1}</p>
                              <FormFieldsComposer
                                name={`${item}`}
                                schema={filteredSchema.items}
                                formName={formName}
                                submitting={submitting}
                              />
                              {isFormError && error[index] && (
                                <FormattedMessage id={error[index]}>
                                  {txt => <span className="ArrayField__item-error">{txt}</span>}
                                </FormattedMessage>
                              )}
                            </div>
                          </div>
                        )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        ) : (
          <NoItemsPanel
            // captionSlug={schema.title}
            // descriptionSlug={schema.description}
            className="FieldArray__no-items"
            isError={!!formError}
          />
        )}

        <style>{style}</style>
        <style>{dynamicStyles}</style>
      </div>
    );
  }
}

MasterGroupArrayField.propTypes = {
  schema: PropTypes.object.isRequired,
  formName: PropTypes.string.isRequired,
  showModal: PropTypes.func.isRequired,
  meta: PropTypes.object.isRequired,
  fields: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  arraysName: PropTypes.string.isRequired,
  submitting: PropTypes.bool,
  theme: PropTypes.object,
  formData: PropTypes.object,
  index: PropTypes.number,
};

MasterGroupArrayField.defaultProps = {
  fields: {},
  submitting: false,
  theme: {},
  formData: {},
  index: 0,
};

// 使用connect从Redux store获取表单数据
const mapStateToProps = (state, ownProps) => {
  const formData = state.form && state.form[ownProps.formName] ? state.form[ownProps.formName].values || {} : {};
  return {
    formData,
  };
};

const ThemedArrayField = compose(connect(mapStateToProps), withTheme(), ModalHOC)(MasterGroupArrayField);

export default ThemedArrayField;
export { MasterGroupArrayField };
